import React, { useState } from 'react';

import ToggleInput from 'CommonComponents/ToggleInput';

import { useToast } from 'Hooks/useToast';

const PROJECT_INFO = 'project_info';

/**
 * Which parts of Project Info this project shows. Hiding only
 * removes them from menus and pages; their data stays and keeps feeding the
 * budget. Each toggle saves on its own and reverts if the save fails.
 *
 * A tile keeps its own setting while Project Info is off, so switching Project
 * Info back on restores exactly the tiles that were showing before.
 *
 * `sections` seeds the panel once; after that the panel's own state is the
 * source of truth, since the parent re-copies its dataset on unrelated edits.
 */
export default function SectionVisibilityPanel({ sections: initialSections, updateUrl }) {
    const [sections, setSections] = useState(initialSections || []);
    const [savingKey, setSavingKey] = useState(null);

    const toast = useToast();

    const setVisible = (key, visible) => setSections((previous) => previous.map((section) => (section.key === key ? { ...section, visible } : section)));

    const toggle = (section) => {
        const next = !section.visible;

        setVisible(section.key, next);
        setSavingKey(section.key);

        axios
            .put(updateUrl, { sections: { [section.key]: next } })
            .then(({ data }) => {
                setSections(data.data?.sections || []);
                toast.success(next ? $t('{{section}} is now shown', { section: section.label }) : $t('{{section}} is now hidden', { section: section.label }));
            })
            .catch((e) => {
                setVisible(section.key, section.visible);
                toast.error(
                    e.response?.status === 403
                        ? $t('You do not have permission to change section visibility')
                        : e.response?.data?.message || $t('Something went wrong'),
                );
            })
            .finally(() => setSavingKey(null));
    };

    const master = sections.find((section) => section.key === PROJECT_INFO);
    const tiles = sections.filter((section) => section.parent === PROJECT_INFO);

    const renderToggle = (section, disabled = false) => (
        <ToggleInput
            key={section.key}
            label={section.label}
            checked={!!section.visible}
            disabled={disabled || savingKey !== null}
            onChange={() => toggle(section)}
        />
    );

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-3 tw-rounded-lg tw-border-2 tw-border-gray-200 tw-p-2.5">
            <div className="tw-flex tw-flex-col tw-gap-y-1">
                <div className="tw-font-semibold">{$t('Visible sections')}</div>
                <p className="tw-text-2xs tw-text-charcoal-50">
                    {$t('Hidden sections are removed from menus and pages for everyone on this project. Their data is kept and still used in budgets.')}
                </p>
            </div>

            {!!master && (
                <div className="tw-flex tw-flex-col">
                    {renderToggle(master)}
                    <div className="tw-flex tw-flex-col tw-ps-4 tw-border-s-2 tw-border-charcoal-15">
                        {tiles.map((tile) => renderToggle(tile, !master.visible))}
                    </div>
                </div>
            )}
        </div>
    );
}
